import * as THREE from 'three';
import { DEVICE } from './device';
import { installNetwork } from './system/net';
import { Guard, registerUpdates } from './system/guard';
import { showFallback } from './fallback';
import { Studio } from './studio/studio';
import { GAME } from './features';
import type { PanelId } from './studio/content';
import './studio/studio.css';

// The shell every visit needs: the renderer, the guard, the studio, toasts and the loading screen, and the hand-offs
// between the studio and the city. The city itself (city.ts, with Rapier) is a separate chunk: loadCity() fetches it
// when the visitor heads for the streets, or in the background once the studio is up (main.ts), never with the studio.

// every fetch and three.js load from here on: versioned URLs and retries (system/net.ts)
installNetwork();

export const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
export const canvas = $<HTMLCanvasElement>('world');
export const loading = $('loading');
export const fade = $('fade');
const toast = $('toast');
export const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const loadingText = (t: string) => { (loading.querySelector('span') as HTMLElement).textContent = t; };

export function showToast(message: string, duration = 2400) {
  toast.textContent = message;
  toast.classList.add('show');
  window.setTimeout(() => toast.classList.remove('show'), duration);
}

// no WebGL at all (very old phones, GPU blocklisted): a static card with the essentials instead of a blank page
if (!DEVICE.webgl) { showFallback(); throw new Error('WebGL is not available'); }
export const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(DEVICE.cityPixelRatio);
renderer.setSize(innerWidth, innerHeight);
renderer.shadowMap.enabled = !DEVICE.safe;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.15;
addEventListener('resize', () => renderer.setSize(innerWidth, innerHeight));
// errors, GPU resets, the resolution governor, crash log, updates (system/guard.ts)
export const guard = new Guard(renderer);
registerUpdates();

// Salman's studio, a separate baked interior scene; the city pauses while you are inside
export const studio = new Studio(renderer, (m, ms) => showToast(m, ms));

/** What the city does at the hand-offs; city.ts fills these in when it loads. */
export const cityHooks = {
  ready: () => false,
  leave: () => {},
  arrive: () => {},
  frame: (_now: number) => {},
};
export const ui = { transitioning: false };

// ------------------------------------------------------------------ the city chunk

let cityChunk: Promise<typeof import('./city')> | null = null;
/** Download and run the city's code (cheap: its top level only sets up the scene and the controls). */
export function loadCity() { return (cityChunk ??= import('./city')); }

let cityStarted = false;
/** Build the city (a no-op once it is building): physics, the world, traffic. */
export function startCity() {
  if (cityStarted || !GAME) return;
  cityStarted = true;
  if (!studio.active) guard.stage('city-loading');
  void loadCity().then((city) => city.init()).catch((error: unknown) => {
    console.error(error);
    loadingText('The city could not load.');
    guard.fail('city', error);
  });
}

// ------------------------------------------------------------------ studio enter / exit

export async function enterStudio(panel?: PanelId) {
  if (studio.active || ui.transitioning) return;
  ui.transitioning = true;
  cityHooks.leave();
  fade.classList.add('on');
  await Promise.all([wait(450), studio.load((f) => loadingText(`Opening the studio ${Math.round(f * 100)}%`))]);
  guard.stage('studio');
  document.body.classList.add('in-studio');
  loading.classList.add('hidden');
  await studio.enter();
  history.replaceState(null, '', '#studio');
  if (panel) studio.openPanel(panel);
  fade.classList.remove('on');
  ui.transitioning = false;
}

studio.onExit = () => {
  if (!GAME) return;   // the studio never leaves for the city while the game is not live
  startCity();
  ui.transitioning = true;
  fade.classList.add('on');
  window.setTimeout(() => {
    document.body.classList.remove('in-studio');
    history.replaceState(null, '', location.pathname + location.search + '#drive');   // a reload stays in the city
    const ready = cityHooks.ready();
    if (!ready) { loading.classList.remove('hidden'); loadingText('Loading the city…'); }
    guard.stage(ready ? 'city' : 'city-loading');
    cityHooks.arrive();
    fade.classList.remove('on');
    ui.transitioning = false;
  }, 450);
};
